import { ChevronDownIcon, FileTextIcon, GlobeIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { cn } from '@/lib/utils';
import type { ActiveSourceReference } from '../chat-experience';
import {
  formatSourceLocation,
  getDocumentDisplayName,
  getSourceLocation,
  type DocumentSourceEntry,
} from '../chat-document-sources';
import {
  getWebSourceDisplayAddress,
  getWebSourceSiteName,
  orderWebSourcesForList,
  type WebSourcePresentation,
} from '../chat-web-citations';

const ROW_CLASS_NAME = 'group/source flex w-full min-w-0 items-start gap-2.5 rounded-md px-2 py-1.5 text-left no-underline transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type ChatSourcesDisclosureProps = {
  /** Web keys in order of first citation in the answer. */
  citedWebKeys: readonly string[];
  documents: readonly DocumentSourceEntry[];
  onOpenDocument: (
    document: ChatDocumentDTO,
    reference?: ActiveSourceReference | null,
    references?: ActiveSourceReference[] | null,
  ) => void;
  webSources: readonly WebSourcePresentation[];
};

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "Page 30", "Pages 30, 12" or "Diapositive 3", then the cited section if known. */
function describeDocumentPassages(references: readonly ActiveSourceReference[]) {
  const locations = references.flatMap((reference) => getSourceLocation(reference) ?? []);
  const uniqueLocations = locations.filter((location, index) => (
    locations.findIndex((other) => other.kind === location.kind && other.number === location.number) === index
  ));
  const [first] = uniqueLocations;
  const locationLabel = !first
    ? null
    : uniqueLocations.length === 1 || uniqueLocations.some((location) => location.kind !== first.kind)
      ? capitalize(formatSourceLocation(first, 'long'))
      : `${first.kind === 'slide' ? 'Diapositives' : 'Pages'} ${uniqueLocations.map((location) => location.number).join(', ')}`;
  const section = references[0]?.sectionPath?.filter(Boolean).join(' › ');
  return [locationLabel, section].filter(Boolean).join(' · ') || 'Passage cité';
}

function SourceRowContent({ icon, name, detail }: { detail: string; icon: ReactNode; name: string }) {
  return (
    <>
      <span aria-hidden="true" className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground underline-offset-4 group-hover/source:underline">{name}</span>
        <span className="block truncate text-xs text-muted-foreground" title={detail}>{detail}</span>
      </span>
    </>
  );
}

/**
 * Single collapsed source list under an answer, one row per source: consulted
 * pages open in a new tab, cited company documents open the in-app viewer on
 * their first passage. No third-party favicon is loaded (sovereignty).
 */
export function ChatSourcesDisclosure({ citedWebKeys, documents, onOpenDocument, webSources }: ChatSourcesDisclosureProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const pages = orderWebSourcesForList(webSources, citedWebKeys);
  if (pages.length === 0 && documents.length === 0) {
    return null;
  }

  return (
    <div className="not-prose flex flex-col gap-2">
      <div className="flex items-center">
        <button
          aria-controls={panelId}
          aria-expanded={isOpen}
          className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-muted/25 px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setIsOpen((current) => !current)}
          type="button"
        >
          {isOpen ? 'Masquer les sources' : 'Voir les sources'}
          <ChevronDownIcon
            aria-hidden="true"
            className={cn('size-3.5 transition-transform', isOpen && 'rotate-180')}
          />
        </button>
      </div>
      {isOpen ? (
        <div className="flex max-w-2xl flex-col gap-3" id={panelId}>
          {pages.length > 0 ? (
            <section aria-labelledby={`${panelId}-web`} className="flex flex-col gap-1">
              <h3 className="px-2 text-xs font-medium text-muted-foreground" id={`${panelId}-web`}>
                Sources consultées
              </h3>
              <ul className="flex flex-col gap-0.5">
                {pages.map((page) => {
                  const siteName = getWebSourceSiteName(page.url);
                  const detail = page.title ?? getWebSourceDisplayAddress(page.url);
                  return (
                    <li key={page.url}>
                      <a
                        aria-label={`${siteName} : ${detail}`}
                        className={ROW_CLASS_NAME}
                        href={page.url}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <SourceRowContent detail={detail} icon={<GlobeIcon className="size-3" />} name={siteName} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
          {documents.length > 0 ? (
            <section aria-labelledby={`${panelId}-documents`} className="flex flex-col gap-1">
              <h3 className="px-2 text-xs font-medium text-muted-foreground" id={`${panelId}-documents`}>
                Documents
              </h3>
              <ul className="flex flex-col gap-0.5">
                {documents.map((entry) => {
                  const name = getDocumentDisplayName(entry.document);
                  const location = getSourceLocation(entry.reference);
                  return (
                    <li key={entry.key}>
                      <button
                        aria-label={location ? `Ouvrir ${name}, ${formatSourceLocation(location, 'long')}` : `Ouvrir ${name}`}
                        className={ROW_CLASS_NAME}
                        data-testid="citation-chip"
                        onClick={() => onOpenDocument(entry.document, entry.reference, entry.references)}
                        type="button"
                      >
                        <SourceRowContent
                          detail={describeDocumentPassages(entry.references)}
                          icon={<FileTextIcon className="size-3" />}
                          name={name}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
